import { z } from 'zod'

import {
  FORM_VALIDATION_ERROR_MESSAGES,
  NAME_ERROR_MESSAGE,
  NAME_VALIDATION_REGEX,
  PHONE_REGEXP,
} from '@/constants'
import { passwordContainsPhonePart } from '@/utils/auth/passwordValidation'

const {
  REQUIRED_FIELD_MESSAGE,
  PHONE_WRONG_FORMAT_MESSAGE,
  EMAIL_WRONG_FORMAT_MESSAGE,
} = FORM_VALIDATION_ERROR_MESSAGES

const passwordSchema = z
  .string()
  .min(1, REQUIRED_FIELD_MESSAGE)
  .min(6, 'Пароль должен содержать минимум 6 символов')

export const baseSchema = z.object({
  phoneNumber: z
    .string({ required_error: REQUIRED_FIELD_MESSAGE })
    .trim()
    .min(1, REQUIRED_FIELD_MESSAGE)
    .regex(PHONE_REGEXP, {
      message: PHONE_WRONG_FORMAT_MESSAGE,
    }),
  email: z
    .string()
    .trim()
    .optional()
    .refine((value) => !value || z.string().email().safeParse(value).success, {
      message: EMAIL_WRONG_FORMAT_MESSAGE,
    }),
  name: z
    .string({ required_error: REQUIRED_FIELD_MESSAGE })
    .trim()
    .min(1, REQUIRED_FIELD_MESSAGE)
    .regex(NAME_VALIDATION_REGEX, { message: NAME_ERROR_MESSAGE }),
  surname: z
    .string({ required_error: REQUIRED_FIELD_MESSAGE })
    .trim()
    .min(1, REQUIRED_FIELD_MESSAGE)
    .regex(NAME_VALIDATION_REGEX, { message: NAME_ERROR_MESSAGE }),
  patronymic: z
    .string()
    .trim()
    .optional()
    .refine((val) => !val || NAME_VALIDATION_REGEX.test(val), {
      message: NAME_ERROR_MESSAGE,
    }),
  password: passwordSchema,
  repeatPassword: passwordSchema,
  paymentType: z.object(
    { value: z.string().trim(), label: z.string().trim() },
    { required_error: REQUIRED_FIELD_MESSAGE }
  ),
  privacyPolicyAgreement: z
    .boolean({
      required_error: 'Дайте свое согласие с политикой конфиденциальности',
    })
    .refine((val) => val === true, {
      message: 'Дайте свое согласие с политикой конфиденциальности',
    }),
})

export const physicalCreationSchema = baseSchema
  .refine(
    (data) => !passwordContainsPhonePart(data.password, data.phoneNumber),
    {
      message: 'Пароль не должен содержать номер телефона или его часть',
      path: ['password'],
    }
  )
  .refine((data) => data.password == data.repeatPassword, {
    message: 'Пароли не совпадают',
    path: ['repeatPassword'],
  })
  .refine((data) => data.password == data.repeatPassword, {
    message: 'Пароли не совпадают',
    path: ['password'],
  })

export const legalCreationSchema = baseSchema
  .extend({
    vatNumber: z
      .string()
      .transform((val) => val.replace(/\s/g, ''))
      .refine((val) => val.length === 0 || /^\d{9}$/.test(val), {
        message: 'УНП должен состоять из 9 цифр',
      })
      .optional(),
    description: z
      .string({ required_error: REQUIRED_FIELD_MESSAGE })
      .trim()
      .min(1, REQUIRED_FIELD_MESSAGE),
    shortLegalName: z.string().trim().optional(),
    fullLegalName: z.string().trim().optional(),
    legalAddress: z.string().trim().optional(),
    postalAddress: z.string().trim().optional(),
    ipRegistrationDate: z.string().trim().optional(),
    iban: z
      .string({ required_error: REQUIRED_FIELD_MESSAGE })
      .trim()
      .min(1, REQUIRED_FIELD_MESSAGE)
      .transform((val) => {
        const cleaned = val.replace(/\s/g, '')

        const cyrillicToLatin: Record<string, string> = {
          А: 'A',
          В: 'B',
          Е: 'E',
          К: 'K',
          М: 'M',
          Н: 'H',
          О: 'O',
          Р: 'P',
          С: 'C',
          Т: 'T',
          Х: 'X',
        }

        return cleaned
          .split('')
          .map((char) => cyrillicToLatin[char] || char)
          .join('')
      })
      .refine((val) => val.length === 28, {
        message: 'IBAN должен содержать ровно 28 символов',
      })
      .refine((val) => /^[A-Z0-9]+$/.test(val), {
        message: 'IBAN должен содержать только латинские буквы и цифры',
      }),
    currency: z.object(
      { value: z.string().trim(), label: z.string().trim() },
      { required_error: REQUIRED_FIELD_MESSAGE }
    ),
    parentOrganisationId: z.string().optional(),
  })
  .refine(
    (data) => !passwordContainsPhonePart(data.password, data.phoneNumber),
    {
      message: 'Пароль не должен содержать номер телефона или его часть',
      path: ['password'],
    }
  )
  .refine((data) => data.password == data.repeatPassword, {
    message: 'Пароли не совпадают',
    path: ['repeatPassword'],
  })
  .refine((data) => data.password == data.repeatPassword, {
    message: 'Пароли не совпадают',
    path: ['password'],
  })
// .refine((data) => data.privacyPolicyAgreement == true, {
//   message: 'Дайте свое согласие с политикой конфиденциальности',
// })

export type PhysicalCreationType = z.infer<typeof physicalCreationSchema>

export type LegalCreationType = z.infer<typeof legalCreationSchema>

export const GENERAL_FIELDS = [
  { id: 1, title: 'Фамилия', name: 'surname', required: true },
  { id: 2, title: 'Имя', name: 'name', required: true },
  { id: 7, title: 'Отчество', name: 'patronymic', required: false },
  {
    id: 3,
    title: 'Мобильный / контактный телефон',
    name: 'phoneNumber',
    required: true,
  },
  { id: 4, title: 'Email', name: 'email', required: false },
  { id: 5, title: 'Пароль', name: 'password', required: true },
  {
    id: 6,
    title: 'Подтверждение пароля',
    name: 'repeatPassword',
    required: true,
  },
]

export const LEGAL_FIELDS = [
  {
    id: 1,
    title: 'Наименование организации',
    name: 'description',
    required: true,
  },
  {
    id: 2,
    title: 'Краткое наименование организации (по регистрации)',
    name: 'shortLegalName',
    required: false,
  },
  {
    id: 3,
    title: 'Полное наименование организации (по регистрации)',
    name: 'fullLegalName',
    required: false,
  },
  {
    id: 4,
    title: 'Юридический адрес',
    name: 'legalAddress',
    required: false,
  },
  {
    id: 5,
    title: 'Почтовый адрес',
    name: 'postalAddress',
    required: false,
  },
  {
    id: 6,
    title: 'Дата регистрации предприятия',
    name: 'ipRegistrationDate',
    required: false,
  },
  {
    id: 7,
    title: 'Р/счет',
    name: 'iban',
    required: true,
  },
]
